import {Helmet} from '@dr.pogodin/react-helmet';
import {Alert, Form, FormField, FormSubmitHandler, Separator} from 'platform/components';
import {Box, HStack, Space, VStack} from 'platform/foundation';

import {useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';

import {defaultTo, isNil} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {useGetTenantQuery} from '@dms/api/core';
import {useGetAuthorizationProfilesQuery} from '@dms/api/security';
import {VehicleTypeEnumObject} from '@dms/api/shared';
import {useGetVehicleGroupsQuery} from '@dms/api/vehicle';
import {
  useGetDictionaryQuery,
  useGetLabourRateQuery,
  useGetVehicleClassesQuery,
  usePatchLabourRateMutation,
  usePostLabourRateMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsFooter, SettingsSection, SettingsTemplate} from '@dms/settings/shared';
import {getOptionsFromAuthorizationProfiles, handleApiError, useWorkTypeOptions} from '@dms/shared';
import {selectFuelTypes} from '@dms/teas';

import {ApiError, useNavigate} from 'shared/web';

import {LabourRateFormSchema, LabourRateFormValues} from './types/LabourRateFormSchema';

export function LabourRatesDetail() {
  const {id} = useParams();
  const navigate = useNavigate();
  const {data: tenant} = useGetTenantQuery();
  const {
    data: labourRate,
    isLoading: isLabourRateLoading,
    isError: isLabourRateError,
  } = useGetLabourRateQuery({labourRateId: id ?? ''}, {skip: isNil(id)});
  const {data: rateTypes, isLoading: isRateTypesLoading} = useGetDictionaryQuery({
    category: 'SVCRATETYPE',
  });
  const {data: vehicleGroups, isLoading: isVehicleGroupsLoading} = useGetVehicleGroupsQuery({});
  const {data: authorizationProfiles, isLoading: isAuthorizationProfilesLoading} =
    useGetAuthorizationProfilesQuery({'x-tenant': tenant?.id ?? ''}, {skip: isNil(tenant)});
  const {data: vehicleClasses, isLoading: isVehicleClassesLoading} = useGetVehicleClassesQuery();
  const vehicleFuelOptions = useSelector(
    selectFuelTypes(VehicleTypeEnumObject.VEHICLETYPE_PASSENGER_CAR)
  );
  const [postLabourRate] = usePostLabourRateMutation();
  const [patchLabourRate] = usePatchLabourRateMutation();

  const {getOptionsWithSelectedValues, isLoading: isWorkTypesLoading} = useWorkTypeOptions();

  const handleSubmit: FormSubmitHandler<LabourRateFormValues> = async (
    data,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    const body = {
      ...data,
      rateTypes: data.rateTypes?.filter(isNotNil) ?? [],
      authorizationProfileIds: data.authorizationProfileIds?.filter(isNotNil) ?? [],
      currency: tenant?.currency ?? '',
      workTypeIds: data.workTypeIds?.filter(isNotNil) ?? [],
      vehicleGroupIds: data.vehicleGroupIds?.filter(isNotNil) ?? [],
      vehicleClassIds: data.vehicleClassIds?.filter(isNotNil) ?? [],
      vehicleFuelTypes: data.vehicleFuelTypes?.filter(isNotNil) ?? [],
      isActive: labourRate?.isActive ?? true,
    };

    if (isNil(id)) {
      return await postLabourRate({body})
        .unwrap()
        .then(() => {
          if (blockerContext?.wasSubmittedFromBlocker) {
            return;
          }
          navigate(settingsRoutes.labourRates);
        })
        .catch((e: ApiError) =>
          handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
        );
    }

    await patchLabourRate({labourRateId: id, body})
      .unwrap()
      .then(() => {
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        navigate(settingsRoutes.labourRates);
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  const workTypeOptions = getOptionsWithSelectedValues(labourRate?.vehicleGroupIds);

  const vehicleGroupOptions = vehicleGroups
    ?.filter(
      (group) =>
        group?.isActive || labourRate?.vehicleGroupIds?.includes(group?.vehicleGroupId ?? '')
    )
    .map((group) => ({
      label: group?.name,
      value: group?.vehicleGroupId,
    }));

  const vehicleClassOptions = vehicleClasses
    ?.filter((vehicleClass) => vehicleClass?.isActive)
    .map((vehicleClass) => ({
      label: vehicleClass?.name,
      value: vehicleClass?.vehicleClassId,
    }));

  const titleKey = isNil(id)
    ? 'entity.workshop.actions.newLabourRate'
    : 'entity.workshop.labels.labourRates';

  return (
    <>
      <Helmet title={i18n.t(titleKey)} />
      <SettingsTemplate
        header={{
          title: labourRate?.name ?? i18n.t('entity.workshop.actions.newLabourRate'),
          breadcrumbs: [
            {
              label: i18n.t('entity.workshop.labels.labourRates'),
              href: settingsRoutes.labourRates,
            },
          ],
        }}
        isCreating={isNil(id)}
        isLoading={isLabourRateLoading}
        isError={isLabourRateError}
        data-testid={testIds.settings.labourRatesDetail('template')}
      >
        <SettingsSection>
          <Form<LabourRateFormValues>
            experimentalZodSchema={LabourRateFormSchema}
            defaultValues={{
              name: defaultTo('', labourRate?.name),
              authorizationProfileIds: defaultTo([], labourRate?.authorizationProfileIds),
              workTypeIds: labourRate?.workTypeIds,
              rateTypes: defaultTo([], labourRate?.rateTypes),
              rateUnitPriceWithoutVat: labourRate?.rateUnitPriceWithoutVat ?? undefined,
              vehicleGroupIds: labourRate?.vehicleGroupIds,
              vehicleClassIds: labourRate?.vehicleClassIds,
              vehicleFuelTypes: labourRate?.vehicleFuelTypes,
            }}
            onSubmit={handleSubmit}
            shouldWatchForUnsavedChanges
          >
            {(control, formApi, navigateWithoutBlocker) => (
              <>
                <VStack spacing={4}>
                  <FormField
                    control={control}
                    name="name"
                    type="text"
                    label={i18n.t('general.labels.name')}
                    isRequired
                    data-testid={testIds.settings.labourRatesDetail('name')}
                  />
                  <FormField
                    control={control}
                    name="authorizationProfileIds"
                    type="multiChoice"
                    label={i18n.t('entity.settings.labels.authorizationProfile')}
                    isRequired
                    isLoading={isAuthorizationProfilesLoading}
                    options={getOptionsFromAuthorizationProfiles(
                      authorizationProfiles,
                      formApi.watch('authorizationProfileIds')
                    )}
                    data-testid={testIds.settings.labourRatesDetail('authorizationProfiles')}
                    isNotClearable
                  />
                  <Separator spacing={0} />
                  <HStack spacing={4}>
                    <Box flex={1}>
                      <FormField
                        control={control}
                        name="rateTypes"
                        type="multiChoice"
                        options={rateTypes ?? []}
                        isLoading={isRateTypesLoading}
                        label={i18n.t('entity.order.labels.workRateType')}
                        isRequired
                        data-testid={testIds.settings.labourRatesDetail('rateTypes')}
                        isNotClearable
                      />
                    </Box>
                    <Box flex={1}>
                      <FormField
                        control={control}
                        name="rateUnitPriceWithoutVat"
                        type="number"
                        label={i18n.t('entity.order.labels.rateValueWithoutVat')}
                        isRequired
                        data-testid={testIds.settings.labourRatesDetail('rateValueWithoutVat')}
                      />
                    </Box>
                  </HStack>
                  <HStack spacing={4}>
                    <Box flex={1}>
                      <FormField
                        control={control}
                        name="workTypeIds"
                        type="multiChoice"
                        options={workTypeOptions}
                        isLoading={isWorkTypesLoading}
                        label={i18n.t('entity.order.labels.workType')}
                        data-testid={testIds.settings.labourRatesDetail('workTypes')}
                        isNotClearable
                      />
                    </Box>
                    <Space fillAvailable />
                  </HStack>
                  <Separator spacing={0} />
                  <HStack spacing={4}>
                    <Box flex={1}>
                      <FormField
                        control={control}
                        name="vehicleGroupIds"
                        type="multiChoice"
                        label={i18n.t('entity.workshop.labels.vehicleGroup')}
                        isLoading={isVehicleGroupsLoading}
                        options={vehicleGroupOptions ?? []}
                        data-testid={testIds.settings.labourRatesDetail('vehicleGroups')}
                        isNotClearable
                      />
                    </Box>
                    <Box flex={1}>
                      <FormField
                        control={control}
                        name="vehicleClassIds"
                        type="multiChoice"
                        options={vehicleClassOptions ?? []}
                        isLoading={isVehicleClassesLoading}
                        label={i18n.t('entity.workshop.labels.vehicleClasses')}
                        data-testid={testIds.settings.labourRatesDetail('vehicleClasses')}
                      />
                    </Box>
                  </HStack>
                  <HStack spacing={4}>
                    <Box flex={1}>
                      <FormField
                        control={control}
                        name="vehicleFuelTypes"
                        type="multiChoice"
                        label={i18n.t('entity.vehicle.labels.fuelType')}
                        options={vehicleFuelOptions}
                        data-testid={testIds.settings.labourRatesDetail('vehicleFuelTypes')}
                        isNotClearable
                      />
                    </Box>
                    <Space fillAvailable />
                  </HStack>
                  <Alert
                    variant="info"
                    message={i18n.t('entity.workshop.warnings.labourRateFilterIntersection')}
                    data-testid={testIds.settings.labourRatesDetail('filterIntersectionAlert')}
                  />
                </VStack>
                <SettingsFooter
                  actions={[
                    {
                      type: 'button',
                      title: i18n.t('general.actions.discardChanges'),
                      variant: 'secondary',
                      onClick: () => navigateWithoutBlocker(settingsRoutes.labourRates),
                    },
                    {
                      control,
                      type: 'form-button',
                      buttonType: 'submit',
                      title: i18n.t('general.actions.saveChanges'),
                    },
                  ]}
                  data-testid={testIds.settings.labourRatesDetail('footer')}
                />
              </>
            )}
          </Form>
        </SettingsSection>
      </SettingsTemplate>
    </>
  );
}
